import { MIN_ROW_HEIGHT } from '@modules/dashboard/constants';
import { DashboardRow } from 'generated/graphql';
import { cloneDeep, throttle } from 'lodash';

interface RowResizeControllerParams {
  row: DashboardRow;
  onResize: (row: DashboardRow) => void;
  onResizeEnd: (row: DashboardRow) => void;
  getMaxCellsMinHeight: () => number;
}

export class RowResizeController {
  protected row: DashboardRow;
  private rowResizerYPosition: number = 0;
  private onResize: (row: DashboardRow) => void;
  private onResizeEnd: (row: DashboardRow) => void;
  private getMaxCellsMinHeight: () => number;

  constructor({
    row,
    onResize,
    onResizeEnd,
    getMaxCellsMinHeight,
  }: RowResizeControllerParams) {
    this.resetRow(row);
    this.row = row;
    this.onResize = onResize;
    this.onResizeEnd = onResizeEnd;
    this.getMaxCellsMinHeight = getMaxCellsMinHeight;
  }

  resetRow(row: DashboardRow) {
    this.row = cloneDeep(row);
  }

  getRowHeight(): number {
    return Math.max(this.getMaxCellsMinHeight(), this.row.height);
  }

  private rowResizeHandler = throttle((event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const delta = event.clientY - this.rowResizerYPosition;
    this.rowResizerYPosition = event.clientY;
    const height = this.getRowHeight() + delta;

    if (height >= this.getMaxCellsMinHeight()) {
      const rowClone = cloneDeep(this.row);
      rowClone.height = Math.round(Math.max(MIN_ROW_HEIGHT, height));

      const resizerElement = document.querySelector(
        `[data-id='${this.row.id}']`,
      );
      if (resizerElement) {
        const rect = resizerElement?.getBoundingClientRect();
        if (rect.bottom > window.innerHeight - 25) {
          document.body.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }
      }

      this.onResize(rowClone);
    }
  }, 50);

  private rowResizeStopHandler = () => {
    if (this.getRowHeight() > this.getMaxCellsMinHeight()) {
      this.onResizeEnd(this.row);
    }

    this.disconnect();
  };

  private registerMouseEvents() {
    window.addEventListener('mousemove', this.rowResizeHandler);
    window.addEventListener('mouseup', this.rowResizeStopHandler);
  }

  disconnect() {
    window.removeEventListener('mousemove', this.rowResizeHandler);
    window.removeEventListener('mouseup', this.rowResizeStopHandler);
  }

  startResizingRowHeight = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    this.rowResizerYPosition = event.clientY;

    this.registerMouseEvents();
  };
}
